import React from 'react';
import { Link } from 'react-router-dom';
import { PenTool, Globe, Youtube, MessageSquare, Heart, Mail, ArrowUpRight, ChevronRight, Instagram, Twitter, Facebook } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-r from-indigo-900 to-indigo-800 text-white overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-400"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDMwaC02bDMtMyAzIDN6bS02IDBoNmwtMyAzLTMtM3oiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
      
      <div className="container mx-auto px-4 py-10 relative z-10">
        <div className="flex flex-col md:flex-row justify-between mb-8">
          {/* Logo and main info */}
          <div className="mb-8 md:mb-0 max-w-xs">
            <Link to="/" className="group flex items-center mb-4 transform transition-transform duration-300 hover:translate-x-1">
              <div className="p-2 bg-white/10 rounded-lg mr-3">
                <PenTool className="h-6 w-6 text-indigo-300 group-hover:text-white transition-colors duration-300" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">KangleiCode Tools</span>
                <span className="block text-xs text-indigo-300 mt-0.5">tools.kangleicode.in</span>
              </div>
            </Link>
            <p className="text-sm text-indigo-200 mt-2 mb-4">
              Digital tools to enhance your workflow. Create, optimize, and share your content with our powerful suite of tools.
            </p>
            
            {/* Social icons */}
            <div className="flex space-x-4 mt-4">
              {[Instagram, Twitter, Facebook, Youtube].map((Icon, index) => (
                <a key={index} href="#" className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors duration-300">
                  <Icon className="h-5 w-5 text-indigo-300 hover:text-white transition-colors duration-300" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Navigation columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-md font-semibold text-white mb-4 flex items-center">
                <Globe className="h-4 w-4 mr-2 text-indigo-300" />
                Our Tools
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/youtube-reel" className="text-indigo-200 hover:text-white transition-colors flex items-center group">
                    <Youtube className="h-4 w-4 mr-2 text-indigo-400 group-hover:text-white transition-colors" />
                    <span>YouTube Reel Creator</span>
                  </Link>
                </li>
                <li>
                  <Link to="/form-connect" className="text-indigo-200 hover:text-white transition-colors flex items-center group">
                    <MessageSquare className="h-4 w-4 mr-2 text-indigo-400 group-hover:text-white transition-colors" />
                    <span>WhatsApp FormConnect</span>
                  </Link>
                </li>
                <li className="text-indigo-300/70 flex items-center">
                  <span className="px-2 py-0.5 text-xs bg-indigo-700/40 rounded-md mr-2">SOON</span>
                  <span>More exciting tools</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-md font-semibold text-white mb-4 flex items-center">
                <Heart className="h-4 w-4 mr-2 text-indigo-300" />
                Resources
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/help" className="text-indigo-200 hover:text-white transition-colors flex items-center">
                    Help Center
                    <ChevronRight className="h-3 w-3 ml-1 opacity-70" />
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-indigo-200 hover:text-white transition-colors flex items-center">
                    Contact Us
                    <ChevronRight className="h-3 w-3 ml-1 opacity-70" />
                  </Link>
                </li>
                <li>
                  <a href="https://kangleicode.in" target="_blank" rel="noopener noreferrer" className="text-indigo-200 hover:text-white transition-colors flex items-center">
                    Main Website
                    <ArrowUpRight className="h-3 w-3 ml-1" />
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-md font-semibold text-white mb-4 flex items-center">
                <Mail className="h-4 w-4 mr-2 text-indigo-300" />
                Company
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-indigo-200 hover:text-white transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-indigo-200 hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-indigo-200 hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Free badge */}
        <div className="flex justify-center my-6">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-700/30 text-indigo-200 font-medium text-sm border border-indigo-600/30">
            <span className="mr-2">✨</span> All tools are currently 100% FREE!
          </div>
        </div>
        
        {/* Copyright bar */}
        <div className="border-t border-indigo-700/50 mt-4 pt-6 text-sm text-indigo-300 flex flex-col md:flex-row justify-between items-center">
          <p>© {currentYear} <a href="https://kangleicode.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">KangleiCode</a>. All rights reserved.</p>
          <p className="mt-2 md:mt-0 text-xs">
            <span className="px-2 py-1 bg-indigo-800/50 rounded-md">Built with <span className="text-red-400">♥</span> in India</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;